import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Heart, HandHeart, ShieldCheck, Sparkles, Sun, MessagesSquare, ArrowRight, DoorOpen, Loader2,
} from "lucide-react";
import { AppHeader } from "../../components/AppHeader";
import { Button } from "../../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const CORE_VALUES = [
  { icon: Heart, title: "Heart-Centered Leadership",
    body: ["We lead with humanity. We believe leadership begins with how we treat people — our clients, families, coworkers, community partners, and ourselves.",
      "Heart-centered does not mean the absence of accountability. It means we can hold people accountable while still honoring their humanity."] },
  { icon: HandHeart, title: "Empowerment Through Service & Collaboration",
    body: ["We don't enter people's lives believing we are there to save them. We help people recognize and strengthen their own capacity.",
      "We collaborate with clients, families, treatment teams, schools, community partners, and one another because sustainable change rarely happens in isolation."] },
  { icon: ShieldCheck, title: "Integrity & Accountability",
    body: ["We do what we say we are going to do. We document what actually happened. We communicate when something goes wrong. We take responsibility for our work.",
      "In mental and behavioral health services, integrity is not simply a company value — people and families depend on it."] },
  { icon: Sparkles, title: "Innovation & Growth",
    body: ["We expect to learn. We expect to evolve. We welcome new ideas, reflection, feedback, training, and opportunities to become better at what we do.",
      "Growth requires the willingness to examine yourself as much as you examine the work."] },
  { icon: Sun, title: "Joyful & Intentional Living",
    body: ["We take the work seriously without believing everything has to feel heavy. We believe in joy. We believe in connection. We believe in celebrating progress.",
      "And we believe the professionals helping others build healthier lives should also be intentional about how they are building their own."] },
  { icon: MessagesSquare, title: "Community & Communication",
    body: ["We are a community. That means we communicate. We participate. We collaborate. We show up. We support one another while maintaining healthy professional boundaries.",
      "GIC&E has continued growing for more than a decade because relationships and community have remained central to who we are."] },
];

const ASK_YOURSELF = [
  "Are you interested in becoming part of a professional community — not simply acquiring another source of income?",
  "Do you believe meaningful mental and behavioral health work requires connection, accountability, compassion, boundaries, and integrity?",
  "Are you willing to learn? Are you willing to be coached?",
  "Are you willing to communicate? Are you willing to participate in community?",
  "Are you willing to serve people without reducing them to diagnoses or billing codes?",
];

const OPTIONS = [
  { value: "YES", label: "YES — I believe my values align, and I would like to continue the application." },
  { value: "UNSURE", label: "I'M NOT SURE — I would like to continue and learn more about GIC&E." },
  { value: "NO", label: "NO — I do not believe this type of professional community is what I am currently looking for." },
];

function Prose({ children }) {
  return <p className="text-[17px] leading-relaxed text-[#3B463F]">{children}</p>;
}

export default function CultureGate() {
  const [app, setApp] = useState(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/application/me");
        if (["ALIGNED", "UNSURE"].includes(data.culture_alignment)) { navigate("/portal", { replace: true }); return; }
        setApp(data);
      } catch { toast.error("Could not load your application."); }
    })();
  }, [navigate]);

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      const { data } = await api.post("/application/me/culture", { response: choice });
      if (choice === "NO") { setApp(data); }
      else { navigate("/portal", { replace: true }); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save your response"); }
    finally { setBusy(false); }
  };

  if (!app) return <div className="min-h-screen bg-[#FAF9F6]" />;

  // Exit state for self-selected-out applicants
  if (app.culture_alignment === "SELF-SELECTED OUT") {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <AppHeader />
        <main className="mx-auto flex max-w-2xl flex-col items-center px-4 py-20 text-center sm:px-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#EAF0ED] text-[#2C5545]"><DoorOpen className="h-7 w-7" /></div>
          <h1 className="mt-6 font-heading text-3xl font-bold text-[#1F2924]">Thank you for your honesty.</h1>
          <p className="mt-4 text-[17px] leading-relaxed text-[#3B463F]">
            Finding the right professional environment matters. Based on your response, GIC&E's community-centered
            model may not align with what you're looking for at this time.
          </p>
          <p className="mt-3 text-[17px] leading-relaxed text-[#3B463F]">
            We appreciate your interest and wish you success in finding an opportunity that better matches your
            professional goals.
          </p>
          <Button onClick={logout} data-testid="exit-application-btn"
            className="mt-8 rounded-full bg-[#2C5545] px-8 py-6 text-base font-semibold hover:bg-[#224336]">
            Exit Application
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader />

      {/* Hero */}
      <div className="relative overflow-hidden bg-[#2C5545] text-white">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #ffffff 0, transparent 40%), radial-gradient(circle at 80% 60%, #ffffff 0, transparent 35%)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-sm uppercase tracking-[0.2em] text-white/70">
            A moment before we begin
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="mt-4 font-heading text-4xl font-extrabold tracking-tight sm:text-6xl">
            Before you apply…
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mx-auto mt-5 max-w-xl text-lg text-white/85">
            First, we want you to know who we are.
          </motion.p>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-6 py-14 sm:py-16">
        {/* Intro narrative */}
        <section className="space-y-5">
          <Prose>At GIC&E, qualifications matter. Degrees matter. Experience matters. Documentation matters. Compliance matters.</Prose>
          <Prose>But before we ask you about any of those things, we need to talk about something even more important:</Prose>
          <p className="font-heading text-3xl font-bold text-[#D56A47]">Your heart.</p>
          <Prose>
            GIC&E is a heart-centered mental and behavioral health community built by life coaches, for life coaches,
            and for professionals who believe that meaningful change happens through genuine human connection.
          </Prose>
          <div className="rounded-2xl border border-[#E5E2DC] bg-white p-7">
            <p className="text-[17px] leading-relaxed text-[#1F2924]">
              We don't simply provide services. <br />We build relationships. We develop leaders. We serve families.
              We strengthen communities.
            </p>
            <p className="mt-3 text-[17px] leading-relaxed text-[#3B463F]">And we are very intentional about who we invite into that work.</p>
          </div>
        </section>

        {/* More than hours */}
        <section className="mt-14">
          <h2 className="font-heading text-2xl font-bold text-[#1F2924]">This is more than a place to pick up hours</h2>
          <div className="mt-4 space-y-4">
            <Prose>Let's be transparent from the beginning.</Prose>
            <Prose>
              If you are looking only for a place to pick up a few extra hours, collect a check, see clients, submit
              notes, and go home, GIC&E may not be the right community for you. And that's okay. We would rather be
              clear before you apply.
            </Prose>
            <Prose>
              GIC&E is a community-based organization in every sense of the word. Our coaches and professionals serve
              people where life actually happens — in homes, schools, communities, and other natural environments.
            </Prose>
            <Prose>
              But "community-based" also describes how we operate with one another. We have family-centered events. We
              create experiences for our coaches. We celebrate together. We learn together. We collaborate. We serve
              our communities together.
            </Prose>
            <Prose>
              As we grow, we are intentionally building a community of professionals who want to be connected to
              something larger than a caseload. We want people who want to help create change.
            </Prose>
          </div>
        </section>

        {/* Life coaching */}
        <section className="mt-14">
          <h2 className="font-heading text-2xl font-bold text-[#1F2924]">A life coaching company at our core</h2>
          <div className="mt-4 space-y-4">
            <Prose>
              Life coaching is part of the foundation of GIC&E. We believe people are more than diagnoses, problems,
              behaviors, circumstances, or billing codes.
            </Prose>
            <Prose>
              Our work is rooted in helping people recognize who they are, strengthen their skills, discover
              possibilities, build healthier lives, and move toward meaningful change.
            </Prose>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl bg-[#EAF0ED] p-5">
                <p className="font-heading font-semibold text-[#2C5545]">Already a trained or certified life coach?</p>
                <p className="mt-1 text-[15px] text-[#3B463F]">Wonderful. We want to know more about your training and experience.</p>
              </div>
              <div className="rounded-xl bg-[#FBF2ED] p-5">
                <p className="font-heading font-semibold text-[#D56A47]">Not a life coach yet?</p>
                <p className="mt-1 text-[15px] text-[#3B463F]">That's okay too. It is not required to begin the application unless required for a specific role.</p>
              </div>
            </div>
            <Prose>
              Eligible team members may have an opportunity, after completing their first 90 days and meeting applicable
              company requirements, to participate in GIC&E's designated life coach training program at no charge.
            </Prose>
          </div>
        </section>

        {/* Core values */}
        <section className="mt-16">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.18em] text-[#515E58]">Please read each value carefully</p>
            <h2 className="mt-2 font-heading text-3xl font-bold text-[#1F2924]">Our Core Values</h2>
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {CORE_VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div key={v.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="rounded-2xl border border-[#E5E2DC] bg-white p-6" data-testid={`value-card-${i}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF0ED] text-[#2C5545]"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 font-heading text-lg font-bold text-[#1F2924]">{v.title}</h3>
                  {v.body.map((b, j) => <p key={j} className="mt-2 text-[15px] leading-relaxed text-[#3B463F]">{b}</p>)}
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Ask yourself */}
        <section className="mt-16 rounded-2xl bg-[#1F2924] p-8 text-white sm:p-10">
          <h2 className="font-heading text-2xl font-bold">Now ask yourself…</h2>
          <p className="mt-3 text-lg text-white/85">Do these values sound like you?</p>
          <ul className="mt-5 space-y-3">
            {ASK_YOURSELF.map((q, i) => (
              <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-white/90">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D56A47]" />{q}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-lg font-medium text-white">
            And most importantly: do you believe you can bring both your professional skills <em>and</em> your humanity into this work?
          </p>
          <p className="mt-3 text-white/85">If your answer is yes, we would love to learn more about you.</p>
        </section>

        {/* Required alignment question */}
        <section className="mt-12 rounded-2xl border-2 border-[#2C5545]/30 bg-white p-8 shadow-sm" data-testid="culture-question">
          <p className="text-xs uppercase tracking-[0.14em] text-[#515E58]">Required</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-[#1F2924]">
            Do you believe your personal and professional values align with the GIC&E Core Values and the community described above?
          </h2>

          <RadioGroup value={choice} onValueChange={setChoice} className="mt-6 space-y-3">
            {OPTIONS.map((o) => (
              <label key={o.value}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${choice === o.value ? "border-[#2C5545] bg-[#EAF0ED]" : "border-[#E5E2DC] hover:bg-[#F4F2EC]"}`}>
                <RadioGroupItem value={o.value} className="mt-0.5" data-testid={`culture-${o.value}`} />
                <span className="text-[15px] leading-relaxed text-[#1F2924]">{o.label}</span>
              </label>
            ))}
          </RadioGroup>

          {/* Dynamic messaging */}
          {choice === "UNSURE" && (
            <div className="mt-5 rounded-xl bg-[#FDF6EA] p-5 text-[15px] leading-relaxed text-[#7A5A15]" data-testid="culture-msg-unsure">
              That's okay. Part of the application process is learning whether GIC&E is right for you — and whether you
              are right for GIC&E. You may continue the application. We simply ask that you keep these values in mind as
              you move through the process.
            </div>
          )}
          {choice === "NO" && (
            <div className="mt-5 rounded-xl bg-[#FBEDED] p-5 text-[15px] leading-relaxed text-[#8A3A3A]" data-testid="culture-msg-no">
              Thank you for your honesty. Finding the right professional environment matters. Based on your response,
              GIC&E's community-centered model may not align with what you're looking for at this time. We appreciate
              your interest and wish you success in finding an opportunity that better matches your professional goals.
            </div>
          )}

          {choice && (
            <Button onClick={submit} disabled={busy} data-testid="culture-continue-btn"
              className={`mt-6 w-full gap-2 rounded-full py-6 text-base font-semibold transition-transform hover:-translate-y-[1px] ${choice === "NO" ? "bg-[#8A3A3A] hover:bg-[#732f2f]" : "bg-[#2C5545] hover:bg-[#224336]"}`}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {choice === "YES" && (<>Yes, let's continue <ArrowRight className="h-5 w-5" /></>)}
              {choice === "UNSURE" && (<>Continue &amp; learn more <ArrowRight className="h-5 w-5" /></>)}
              {choice === "NO" && (<>Exit application <DoorOpen className="h-5 w-5" /></>)}
            </Button>
          )}
        </section>
      </main>
    </div>
  );
}
